import {
  CMInfoFormInput,
  CMInfoSubtool,
} from "../../../modules/CMInfo/CMInfo.hooks";
import { Script, ScriptType } from "../../api/models";
import { reportError } from "../../bugReporting";

export enum CMInfoScriptName {
  CMInfoLanguage = "cmInfoLanguage",
  CMInfoBillingAddress = "cmInfoBillingAddress",
  CMInfoEmail01 = "cmInfoEmail01",
  CMInfoEmail02 = "cmInfoEmail02",
  CMInfoContactPreference = "cmInfoContactPreference",
}

const cmInfoLanguageScript = (): Script[] => [
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "We are required by New York City rules to request and record your language preference. All of our communications are in English. " +
      "We do not provide any language access services. A translation and description of commonly used debt collection terms are available in multiple " +
      "languages on the New York City Department of Consumer Affairs' website at www.nyc.gov/dca. What is your language preference?",
  },
];

const cmInfoBillingAddressScript = (): Script[] => [
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "I'd be happy to help you with that:\nMay I have your current address including apt # please?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If Cardmember can't verify at least the street name and city, transfer to Security ext 6777",
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue: "May I have your new address including apt # please?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "Have Cardmember spell the address/street name to you if it is difficult.",
      },
    ],
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "Click the Update button below.",
      },
    ],
  },
];

const cmInfoEmailScript01 = (initialFormState: CMInfoFormInput): Script[] => {
  const email = initialFormState[CMInfoSubtool.Email].primaryEmail
    ? initialFormState[CMInfoSubtool.Email].primaryEmail
    : initialFormState[CMInfoSubtool.Email].alternateEmail;
  const scripts: Script[] = email
    ? [
        {
          scriptType: ScriptType.Dialogue,
          dialogue: "And please verify your email addresses?",
        },
        {
          scriptType: ScriptType.Instruction,
          instructions: [
            {
              text: "If Cardmember says he/she does not have email address.",
            },
          ],
        },
        {
          scriptType: ScriptType.Dialogue,
          dialogue: `We currently have an email address at ${email.split("@")[1] ?? ""} for you on file. Would you please verify that email address?`,
        },
        {
          scriptType: ScriptType.Instruction,
          instructions: [
            {
              text: "If Cardmember wants to opt out email contact or remove all email addresses.",
            },
          ],
        },
      ]
    : [
        {
          scriptType: ScriptType.Dialogue,
          dialogue:
            "And do you have an Email address that you would like us to use if we need to contact you about information on your account?",
        },
        {
          scriptType: ScriptType.Instruction,
          instructions: [
            {
              text: "If Cardmember wants to opt out email contact or remove all email addresses.",
            },
          ],
        },
      ];

  scripts.push(
    initialFormState[CMInfoSubtool.Email].isPaperless
      ? {
          scriptType: ScriptType.Dialogue,
          dialogue:
            "The Bank is committed to ensure that your privacy is protected and as a result we do not provide your email address(es) " +
            "to third parties. Because you have indicated that you want to receive online billing statements, the Bank will send you a " +
            "notice each month notifying you when your statement is available.\n\n" +
            "If you choose to stop receiving email notifications from us, you will start receiving billing statements in the mail. Are you " +
            "sure you do not want to receive information from us via email?",
        }
      : {
          scriptType: ScriptType.Dialogue,
          dialogue:
            "The Bank is committed to ensure that your privacy is protected and as a result we do not provide your email address(es) to third parties. Additionally, the Bank only sends email notifications about your account or new services we offer. Are you sure you do not want to receive information from us via email?",
        },
  );

  return scripts;
};

const cmInfoEmailScript02 = (): Script[] => [
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If Cardmember provides email or wants to email opt-in.",
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "You previously requested no contact by e-mail, by providing your email address we may " +
      "use it to communicate with you about your account's Credit Line, Due Dates, Collections, etc. Is that ok?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If OK: Uncheck Opt Out of Email Contact",
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue: "And what is the e-mail address?",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: "If Not OK: Leave Opt Out of Email Contact checked",
      },
    ],
  },
];

const cmInfoContactPreferenceScript = (): Script[] => [
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "To find out how our customers would like to hear from us, we are asking that you rank from one to four how " +
      "you would prefer to hear from us - telephone call, email, text message and letter - with one being the most preferred " +
      "and four the least preferred.",
  },
  {
    scriptType: ScriptType.Instruction,
    instructions: [
      {
        text: `In response to a question from the customer - "Is this how you are going to start contacting me"`,
      },
    ],
  },
  {
    scriptType: ScriptType.Dialogue,
    dialogue:
      "Right now, we are trying to gauge our customers' preferences and may be making changes in the future to how we communicate with you.",
  },
];

export const getCMInfoScripts = (
  scriptName: CMInfoScriptName,
  initialFormState: CMInfoFormInput,
): Script[] | undefined => {
  if (!scriptName) {
    return undefined;
  }

  let x: never;
  switch (scriptName) {
    case CMInfoScriptName.CMInfoLanguage:
      return cmInfoLanguageScript();
    case CMInfoScriptName.CMInfoBillingAddress:
      return cmInfoBillingAddressScript();
    case CMInfoScriptName.CMInfoEmail01:
      return cmInfoEmailScript01(initialFormState);
    case CMInfoScriptName.CMInfoEmail02:
      return cmInfoEmailScript02();
    case CMInfoScriptName.CMInfoContactPreference:
      return cmInfoContactPreferenceScript();
    default:
      x = scriptName;
      reportError(
        new Error(
          "Programmer Error: Could not find script associated with the provided scriptName",
          {
            cause: scriptName,
          },
        ),
      );
      return x;
  }
};
